import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { parameterizablePasswordOrAKVtoSecretBase, parameterizableSecretBaseToModel } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { ValueAdapter } from '../value.adapter';
import { GlobalVariables } from 'app/services/globals';
import { serviceNowAuthTypes, ServiceNowLinkedService } from 'shell-models-lib';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(ServiceNowLinkedService),
    disabled: __IS_FABRIC_DPE__,
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                endpoint: '*',
                authenticationType: '*',
                username: '*',
                password: 'secret',
                clientId: '*',
                clientSecret: 'secret',
                useEncryptedEndpoints: '*',
                useHostVerification: '*',
                usePeerVerification: '*',
                encryptedCredential: '*'
            },
            parameters: GlobalVariables.serviceNowParamedLs ? '*' : undefined
        }
    }
})
export class ServiceNowLinkedServiceAdapter extends LinkedServiceAdapter<ServiceNowLinkedService> {
    protected supportFirstClassParameterExperience = GlobalVariables.serviceNowParamedLs;

    protected modelConstructor(): new () => ServiceNowLinkedService {
        return ServiceNowLinkedService;
    }

    protected model(): IModel {
        return ServiceNowLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: ServiceNowLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.ServiceNowLinkedService;
        properties.type = Metamodel.modelFor(ServiceNowLinkedService).name;
        properties.typeProperties = {
            endpoint: this.valueToResource(linkedService, ServiceNowLinkedService.ENDPOINT),
            authenticationType: linkedService.authenticationType,
            username: this.valueToResource(linkedService, ServiceNowLinkedService.USERNAME),
            password: parameterizablePasswordOrAKVtoSecretBase(linkedService, ServiceNowLinkedService.PASSWORD, this.context),
            clientId: linkedService.authenticationType === serviceNowAuthTypes.OAuth2 ?
                this.valueToResource(linkedService, ServiceNowLinkedService.CLIENT_ID) : undefined,
            clientSecret: linkedService.authenticationType === serviceNowAuthTypes.OAuth2 && linkedService.clientSecret ?
                parameterizablePasswordOrAKVtoSecretBase(linkedService, ServiceNowLinkedService.CLIENT_SECRET, this.context) : undefined,
            useEncryptedEndpoints: linkedService.useEncryptedEndpoints,
            useHostVerification: linkedService.useEncryptedEndpoints !== false ? linkedService.useHostVerification : undefined,
            usePeerVerification: linkedService.useEncryptedEndpoints !== false ? linkedService.usePeerVerification : undefined
        };

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: ServiceNowLinkedService): Promise<ServiceNowLinkedService> {
        const properties = linkedServiceResource.properties as Model.ServiceNowLinkedService;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const typeProperties: Model.ServiceNowLinkedServiceTypeProperties = properties.typeProperties;

        ValueAdapter.toModel(linkedService, ServiceNowLinkedService.ENDPOINT, typeProperties.endpoint);
        linkedService.authenticationType = typeProperties.authenticationType;
        ValueAdapter.toModel(linkedService, ServiceNowLinkedService.USERNAME, typeProperties.username);
        parameterizableSecretBaseToModel(
            linkedService,
            ServiceNowLinkedService.PASSWORD,
            properties.typeProperties.password, this.context
        );
        if (typeProperties.authenticationType === serviceNowAuthTypes.OAuth2) {
            ValueAdapter.toModel(linkedService, ServiceNowLinkedService.CLIENT_ID, typeProperties.clientId);
            parameterizableSecretBaseToModel(
                linkedService,
                ServiceNowLinkedService.CLIENT_SECRET,
                properties.typeProperties.clientSecret, this.context
            );
        }
        linkedService.useEncryptedEndpoints = typeProperties.useEncryptedEndpoints;
        linkedService.useHostVerification = typeProperties.useHostVerification;
        linkedService.usePeerVerification = typeProperties.usePeerVerification;

        return linkedService;
    }
}
